import { AxiosError, AxiosResponse } from "axios";
import { Button, Container, Form, Input, Message, Stack } from "basis";
import { postDecreaseCreditLimit } from "library/api";
import { DecreaseCreditCardResponse } from "library/api/types";
import { formatAmount } from "library/moneyUtils";
import { Dispatch, FC } from "react";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import {
  REDUCE_CREDIT_LIMIT_FAILED,
  REDUCE_CREDIT_LIMIT_SUCCEEDED,
} from "./constants";
import { validateNewCreditLimit } from "./reduceCreditLimitUtils";
import {
  Action,
  FormValues,
  IErrors,
  PostDecreaseCreditLimitParams,
  State,
} from "./types";

interface Props {
  state: State;
  dispatch: Dispatch<Action>;
}

export const CreditLimitForm: FC<Props> = ({ state, dispatch }) => {
  const { accountInfo, postReduceCreditLimitHref } = state;

  const decreaseCreditLimitMutation = useMutation<
    AxiosResponse<DecreaseCreditCardResponse>,
    AxiosError,
    PostDecreaseCreditLimitParams
  >(postDecreaseCreditLimit, {
    onSuccess: () => {
      dispatch({
        type: REDUCE_CREDIT_LIMIT_SUCCEEDED,
      });
    },
    onError: (error, { setErrors }) => {
      const errorType = error.response?.data?.type ?? null;
      switch (errorType) {
        case "BAD_REQUEST": {
          setErrors({
            newCreditLimit: "Invalid value",
          });
          break;
        }

        default: {
          dispatch({ type: REDUCE_CREDIT_LIMIT_FAILED, errorType });
        }
      }
    },
  });

  if (!accountInfo || !postReduceCreditLimitHref) {
    return null;
  }

  const { cardMinCreditLimit, currentBalance, creditLimit } = accountInfo;

  const onSubmit = async ({
    errors,
    values,
    setErrors,
  }: {
    errors: IErrors;
    values: FormValues;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setErrors: (error: any) => void;
  }): Promise<void> => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    decreaseCreditLimitMutation.mutate({
      newCreditLimit: +values.newCreditLimit,
      linkUrl: postReduceCreditLimitHref,
      setErrors,
    });
  };

  return (
    <Form initialValues={state.formValues} onSubmit={onSubmit}>
      <Stack gap="8">
        <Container padding="4" bg="grey.t05">
          <CreditCard
            cardName={accountInfo.cardName}
            cardEndsWith={accountInfo.cardEndsWith}
          />
        </Container>
        <Container width="100%" width-sm="400">
          <Input
            name="newCreditLimit"
            label="New credit limit"
            variant="numeric"
            prefix="$"
            validate={validateNewCreditLimit}
            validateData={{
              cardMinCreditLimit,
              currentBalance,
              creditLimit,
            }}
          />
        </Container>
        <Message severity="info-or-minor" bg="grey.t05">
          {currentBalance <= cardMinCreditLimit ? (
            <span data-testid="low-balance-message">
              New credit limit must be at least{" "}
              {formatAmount(cardMinCreditLimit)} (minimum credit limit) and
              below {formatAmount(creditLimit)} (your current credit limit).
            </span>
          ) : (
            <span data-testid="generic-message">
              New credit limit must be at least{" "}
              {formatAmount(currentBalance, { withCents: true })} (your current
              balance) and below {formatAmount(creditLimit)} (your current
              credit limit).
              <br />
              <br />
              To reduce to the minimum credit limit of{" "}
              {formatAmount(cardMinCreditLimit)}, you must first pay your
              current balance down to {formatAmount(cardMinCreditLimit)}.
            </span>
          )}
        </Message>
        {decreaseCreditLimitMutation.isError && (
          <Message severity="blocking" bg="secondary.pink.t30">
            Sorry, there was an error reducing credit limit. Please try again
            later.
          </Message>
        )}
        <Container>
          <Button
            type="submit"
            width="100%"
            width-xs="256"
            loading={decreaseCreditLimitMutation.isLoading}
          >
            Reduce credit limit
          </Button>
        </Container>
      </Stack>
    </Form>
  );
};
